import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./api";

export type Sport = "run" | "cycle" | "swim" | "trail" | "strength" | "hyrox" | "other";
export type SportFilter = Sport[]; // пустой массив = "все"

export function sportsParam(sports: SportFilter): string | undefined {
  if (!sports || sports.length === 0) return undefined;
  return sports.join(",");
}

export type VolumePoint = {
  period_start: string;
  granularity: "day" | "week";
  running_km: number;
  cycling_km: number;
  swimming_km: number;
  other_km: number;
  total_km: number;
  running_min: number;
  cycling_min: number;
  swimming_min: number;
  other_min: number;
  total_minutes: number;
  /** Точный split по каноническим дисциплинам — trail/hyrox/strength/
   *  tri/du больше не тонут в other (аудит 2026-07-07). */
  by_discipline?: Record<string, { km: number; minutes: number }>;
};

export type WellnessPoint = {
  date: string;
  hrv: number | null;
  rhr: number | null;
  sleep_hours: number | null;
  sleep_score: number | null;
  readiness: number | null;
  body_weight: number | null;
  spo2?: number | null;   // T3-8: device spO2 surfaced from intervals raw_data
  steps?: number | null;  // T3-8: daily steps from intervals raw_data
};

export type FitnessPoint = {
  date: string;
  fitness: number | null;
  fatigue: number | null;
  form: number | null;
};

export type LoadPoint = {
  date: string;
  actual_load: number | null;
  planned_load: number | null;
  rpe: number | null;
};

export type DifficultyPoint = {
  date: string;
  workout_id: number;
  title: string | null;
  discipline: string | null;
  level: 1 | 2 | 3 | 4;
  label: string;
  pct_of_ctl: number | null;
  source: "ctl_ratio" | "intensity_factor" | "absolute_load" | "duration_only";
};

export type DifficultyDistribution = {
  counts: Record<"1" | "2" | "3" | "4", number>;
  pcts: Record<"1" | "2" | "3" | "4", number>;
  total: number;
  /**
   * Модель распределения кодом: polarised | grey_zone | pyramidal | mixed.
   * null — тренировок слишком мало, чтобы её называть.
   */
  pattern_code: string | null;
  /** Больше не заполняется бэкендом: раньше сюда шла русская фраза. */
  pattern_hint: string | null;
  points: DifficultyPoint[];
};

export function useDifficulty(range: Range, sports: SportFilter = []) {
  const sp = sportsParam(sports);
  return useQuery({
    queryKey: ["athlete-analytics", "difficulty", range.from, range.to, sp ?? "all"],
    queryFn: async () => {
      const params: Record<string, string> = { from_date: range.from, to_date: range.to };
      if (sp) params.sport = sp;
      const r = await api.get<DifficultyDistribution>("/athlete/analytics/difficulty", { params });
      return r.data;
    },
  });
}

export type Range = { from: string; to: string };

export function useVolume(range: Range, sports: SportFilter = []) {
  const sp = sportsParam(sports);
  return useQuery({
    queryKey: ["athlete-analytics", "volume", range.from, range.to, sp ?? "all"],
    queryFn: async () => {
      const params: Record<string, string> = { from_date: range.from, to_date: range.to };
      if (sp) params.sport = sp;
      const r = await api.get<VolumePoint[]>("/athlete/analytics/volume", { params });
      return r.data;
    },
  });
}

export function useWellness(range: Range) {
  return useQuery({
    queryKey: ["athlete-analytics", "wellness", range.from, range.to],
    queryFn: async () => {
      const r = await api.get<WellnessPoint[]>("/athlete/analytics/wellness", {
        params: { from_date: range.from, to_date: range.to },
      });
      return r.data;
    },
  });
}

export function useFitness(range: Range) {
  return useQuery({
    queryKey: ["athlete-analytics", "fitness", range.from, range.to],
    queryFn: async () => {
      const r = await api.get<FitnessPoint[]>("/athlete/analytics/fitness", {
        params: { from_date: range.from, to_date: range.to },
      });
      return r.data;
    },
  });
}

export function useLoad(range: Range, sports: SportFilter = []) {
  const sp = sportsParam(sports);
  return useQuery({
    queryKey: ["athlete-analytics", "load", range.from, range.to, sp ?? "all"],
    queryFn: async () => {
      const params: Record<string, string> = { from_date: range.from, to_date: range.to };
      if (sp) params.sport = sp;
      const r = await api.get<LoadPoint[]>("/athlete/analytics/load", { params });
      return r.data;
    },
  });
}

export type VO2MaxSourcePoint = {
  source: string;
  value: number;
  age_days: number | null;
  detail: string | null;
};

export type VO2MaxPoint = {
  date: string;
  workout_id: number;
  consensus: number | null;
  primary: string | null;
  disagreement: boolean;
  sources: VO2MaxSourcePoint[];
};

export interface PvFPoint {
  period_start: string;
  granularity: "week" | "month";
  planned_minutes: number;
  actual_minutes: number;
  planned_km: number;
  actual_km: number;
  planned_load: number;
  actual_load: number;
  planned_count: number;
  completed_count: number;
  /** Доля по ШТУКАМ тренировок — про дисциплину («вышел или не вышел»). */
  completion_pct: number;
  /**
   * Доля по ОБЪЁМУ минут — про нагрузку («сколько на самом деле сделал»).
   * Линию на графике рисует именно она: столбики нарисованы в минутах, и
   * процент обязан считаться в тех же единицах. null — плановых минут в
   * периоде нет, делить не на что.
   */
  volume_completion_pct: number | null;
}

export interface PvFSummary {
  points: PvFPoint[];
  avg_completion_pct: number;
  last_period_completion_pct: number | null;
  drift_minutes: number;
  drift_load: number;
}

export function usePlanVsFact(
  days = 84,
  granularity: "week" | "month" = "week",
  sports?: SportFilter,
) {
  // Фильтр дисциплины отвечает на ДРУГОЙ вопрос. Общая доля говорит «сделал
  // восемьдесят процентов»; по дисциплинам видно, что пропускает он ровно
  // плавания. Первое — оценка, второе — понимание.
  const sp = sportsParam(sports ?? []);
  return useQuery<PvFSummary>({
    queryKey: ["athlete-analytics", "plan-vs-fact", days, granularity, sp ?? "all"],
    queryFn: async () => {
      const r = await api.get<PvFSummary>("/athlete/analytics/plan-vs-fact", {
        params: { days, granularity, ...(sp ? { sport: sp } : {}) },
      });
      return r.data;
    },
  });
}


export function useVO2Max(range: Range) {
  return useQuery({
    queryKey: ["athlete-analytics", "vo2max", range.from, range.to],
    queryFn: async () => {
      const r = await api.get<VO2MaxPoint[]>("/athlete/analytics/vo2max", {
        params: { from_date: range.from, to_date: range.to },
      });
      return r.data;
    },
  });
}

export type ThresholdHistoryPoint = {
  date: string;
  test_id: number;
  protocol_code: string;
  sport: string;
  metric_key: "lthr" | "ftp" | "css" | "vlamax";
  value: number;
  unit: string;
  confidence_score: number | null;
  confidence_band: "high" | "medium" | "low" | null;
};

export type ThresholdProtocolStatus = {
  protocol_code: string;
  sport: string;
  last_test_date: string | null;
  days_since_last: number | null;
  recommended_days: number;
  overdue: boolean;
};

export type ThresholdHistoryOut = {
  points: ThresholdHistoryPoint[];
  protocols: ThresholdProtocolStatus[];
};

export function useThresholdTestsHistory(range: Range) {
  return useQuery({
    queryKey: ["athlete-analytics", "threshold-tests-history", range.from, range.to],
    queryFn: async () => {
      const r = await api.get<ThresholdHistoryOut>("/athlete/analytics/threshold-tests-history", {
        params: { from_date: range.from, to_date: range.to },
      });
      return r.data;
    },
  });
}

export type LeaderboardMetric = "weekly_km" | "ctl";

export type LeaderboardEntry = {
  rank: number;
  label: string;
  is_me: boolean;
  value: number | null;
  week_start: string | null;
};

export type LeaderboardOut = {
  metric: LeaderboardMetric;
  total: number;
  my_rank: number | null;
  entries: LeaderboardEntry[];
};

export function useLeaderboard(metric: LeaderboardMetric = "weekly_km") {
  return useQuery<LeaderboardOut>({
    queryKey: ["athlete-leaderboard", metric],
    queryFn: async () => {
      const r = await api.get<LeaderboardOut>("/athlete/analytics/leaderboard", {
        params: { metric },
      });
      return r.data;
    },
    staleTime: 5 * 60_000,
  });
}


// F3 v154 — environment (temperature × HR) for analytics environment lens.
// Backend computes warm/cool HR drift from temperature_celsius column (F2).
//
// Endpoint: GET /athlete/analytics/environment?days=30

export interface EnvironmentPoint {
  date: string;
  temperature_celsius: number;
  avg_hr: number | null;
}

export interface EnvironmentOut {
  days: number;
  workouts_with_temp: number;
  avg_temp_celsius: number | null;
  latest_temp_celsius: number | null;
  hr_drift_warm_minus_cool: number | null;
  warm_threshold_celsius: number;
  expected_hr_drift_bpm: number | null;
  points: EnvironmentPoint[];
}

export function useEnvironmentMetrics(days: number = 30, opts?: { enabled?: boolean }) {
  return useQuery<EnvironmentOut>({
    enabled: opts?.enabled ?? true,
    queryKey: ["athlete-analytics", "environment", days],
    queryFn: async () => {
      const r = await api.get<EnvironmentOut>("/athlete/analytics/environment", {
        params: { days },
      });
      return r.data;
    },
    staleTime: 5 * 60_000,
  });
}


// Trail/run quad-damage: 30-day session history + 7-day cumulative + alert.
// Backend filter: discipline ∈ {trail, run, hyrox} AND status="completed".
// Powers WellnessPage QuadDamageCard.

export interface QuadDamageSession {
  workout_id: number;
  date: string;
  discipline: string;
  elevation_loss_m: number | null;
  quad_damage_load: number | null;
  recovery_recommended_hours: number | null;
}

export interface QuadDamageAlert {
  code: string;
  severity: string;
  cumulative_7d?: number;
  single_session?: number;
}

export interface QuadDamageOut {
  sessions: QuadDamageSession[];
  cumulative_7d: number | null;
  alert: QuadDamageAlert | null;
}

export function useQuadDamage() {
  return useQuery<QuadDamageOut>({
    queryKey: ["athlete-analytics", "quad-damage"],
    queryFn: async () => {
      const r = await api.get<QuadDamageOut>("/athlete/analytics/quad-damage");
      return r.data;
    },
    staleTime: 5 * 60_000,
  });
}


// ТД-41 Fitness_Mass — двухуровневая CTL триатлона.
export interface FitnessMassBreakdown {
  swim_ctl: number;
  bike_ctl: number;
  run_ctl: number;
  strength_ctl: number;
}

export interface FitnessMassOut {
  fitness_mass: number;
  trend_14d_pct: number | null;
  interpretation: "growing" | "stable" | "detraining";
  as_of: string;
  breakdown: FitnessMassBreakdown | null;
  imbalance_flags: string[];
}

export function useFitnessMass(includeBreakdown = true) {
  return useQuery({
    queryKey: ["athlete-analytics", "fitness-mass", includeBreakdown],
    queryFn: async () => {
      const r = await api.get<FitnessMassOut>("/athlete/analytics/fitness-mass", {
        params: { include_breakdown: includeBreakdown },
      });
      return r.data;
    },
    staleTime: 5 * 60_000,
  });
}


// T0-1/T0-2/T0-4 — Foster monotony/strain + Seiler-поляризация за неделю.
// Уже считалось в TrainingLoadSummary (текло в coach-dashboard); теперь
// сериализуется для атлетского блока «Перегрузка» на экране Анализа.
// `unknown` — недель без значения 17% (замер 2026-08-08): тренировок
// меньше двух, монотонность не считается. Раньше приходило "ok".
export type MonotonyFlag = "unknown" | "ok" | "warning" | "red";
export type IntensityPattern =
  | "polarised" | "pyramidal" | "threshold" | "mixed" | "unknown";

export interface ZoneShare {
  zone: string;   // z1..z5
  minutes: number;
  pct: number;
}

export interface PolarizationTriad {
  low_pct: number;   // Z1-2
  mid_pct: number;   // Z3
  high_pct: number;  // Z4-5
}

export type TssSource = "device" | "power" | "hr" | "pace" | "estimated";

export interface TssSourceShare {
  source: TssSource;
  tss: number;
  pct: number;
}

export interface TrainingLoadOut {
  week_end: string;
  sport: string;
  acute_7d: number;
  chronic_per_week: number;
  acwr: number | null;
  acwr_status: string;
  monotony: number | null;
  strain: number | null;
  monotony_flag: MonotonyFlag;
  monotony_warning_threshold: number;
  ctl_now: number;
  ramp_rate_pct_per_week: number | null;
  ramp_flag: "ok" | "caution" | "none";
  ramp_caution_pct: number;
  volume_jump_ratio: number | null;
  volume_jump_flag: "ok" | "warn" | "danger";
  week_load_prev: number;
  week_load_this: number;
  daily_loads: number[];
  intensity_pattern: IntensityPattern;
  zones: ZoneShare[];
  polarization: PolarizationTriad | null;
  polarization_index?: number | null;         // T2-6: Treff 2019 PI (>2 = поляризовано)
  polarization_index_threshold?: number;
  tss_breakdown: TssSourceShare[];
}

export function useTrainingLoad() {
  return useQuery<TrainingLoadOut>({
    queryKey: ["athlete-analytics", "training-load"],
    queryFn: async () => {
      const r = await api.get<TrainingLoadOut>("/athlete/analytics/training-load");
      return r.data;
    },
    staleTime: 5 * 60_000,
  });
}


// T1-1 — durability decay curve: best 5-min power fresh vs after N кДж of work.
// Огибающая по периоду (Maunder 2021; van Erp 2021). Backend отдаёт готовые
// ряды — фронт не считает; сноска-источник в i18n.
export interface DurabilityCurvePoint {
  kj: number;          // накопленная работа, кДж
  fresh_w: number;     // best 5-мин мощность свежим (базлайн периода)
  fatigued_w: number;  // best 5-мин мощность после kj работы
  pct_drop: number;    // (1 - fatigued/fresh) * 100
}

export interface DurabilityCurveOut {
  rides_analysed: number;
  fresh_w: number | null;
  curve: DurabilityCurvePoint[];  // возрастание по кДж; пусто = нет силовых поездок
  stamina_score?: number | null;  // T2-7: % мощности, удержанной утомлённым
}

export function useDurabilityCurve(days = 90) {
  return useQuery<DurabilityCurveOut>({
    queryKey: ["athlete-analytics", "durability-curve", days],
    queryFn: async () => {
      const r = await api.get<DurabilityCurveOut>(
        `/athlete/analytics/durability-curve?days=${days}`,
      );
      return r.data;
    },
    staleTime: 5 * 60_000,
  });
}


// Устойчивость бегуна — тот же слой, ось в километрах: килоджоули считаются
// из ватт, а у бега их нет. Скорость приходит в м/с, а не темпом: темп при
// остановке уходит в бесконечность, и делить в API значило бы отдавать
// экрану число, которое нечем нарисовать. Перевод в темп — на экране.
export interface RunDurabilityPoint {
  km: number;
  fresh_speed_ms: number;
  fatigued_speed_ms: number;
  pct_drop: number;
}

export interface RunDurabilityCurveOut {
  runs_analysed: number;
  fresh_speed_ms: number | null;
  /** База взята с разминки — падение вышло отрицательным, читать нельзя. */
  baseline_suspect?: boolean;
  curve: RunDurabilityPoint[];
}

// Методические развилки, которые выбирает человек. Реестр приходит С
// СЕРВЕРА: список строится по нему, поэтому новая развилка появляется на
// экране сама, а не требует правки интерфейса.
export interface MethodologyOut {
  switches: Record<string, boolean>;
}

export function useMethodology() {
  return useQuery<MethodologyOut>({
    queryKey: ["athlete-analytics", "methodology"],
    queryFn: async () => (await api.get<MethodologyOut>("/athlete/analytics/methodology")).data,
    staleTime: 60_000,
  });
}

export function useSetMethodology() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (switches: Record<string, boolean | null>) =>
      (await api.patch<MethodologyOut>("/athlete/analytics/methodology", { switches })).data,
    onSuccess: () => {
      // Развилка меняет ЧИСЛА, а не оформление: карточки разбора обязаны
      // перечитаться, иначе человек увидит старый декаплинг под новой
      // настройкой и решит, что переключатель не работает.
      qc.invalidateQueries({ queryKey: ["athlete-analytics"] });
      qc.invalidateQueries({ queryKey: ["athlete-decoupling"] });
    },
  });
}

export function useRunDurabilityCurve(days = 90) {
  return useQuery<RunDurabilityCurveOut>({
    queryKey: ["athlete-analytics", "run-durability-curve", days],
    queryFn: async () => {
      const r = await api.get<RunDurabilityCurveOut>(
        `/athlete/analytics/run-durability-curve?days=${days}`,
      );
      return r.data;
    },
    staleTime: 5 * 60_000,
  });
}


// T1-2 — W′-balance по сессиям за период (Skiba 2012): как глубоко атлет
// опустошает анаэробный бак. Session-trace живёт per-workout; здесь — ряд.
export interface WPrimePeriodPoint {
  date: string;
  min_balance_pct: number;  // min(trace)/W′ × 100; ниже = бак опустошён глубже
  depletion_pct: number;    // доля сессии на ≤ 50 % W′
}

export interface WPrimePeriodOut {
  cp_watts: number | null;
  w_prime_j: number | null;
  cp_source: "power_curve" | "ftp" | null;
  series: WPrimePeriodPoint[];  // возрастание по дате; пусто = нет CP/поездок
}

export function useWPrimePeriod(days = 90) {
  return useQuery<WPrimePeriodOut>({
    queryKey: ["athlete-analytics", "wprime-period", days],
    queryFn: async () => {
      const r = await api.get<WPrimePeriodOut>(
        `/athlete/analytics/wprime-period?days=${days}`,
      );
      return r.data;
    },
    staleTime: 5 * 60_000,
  });
}


// T1-3 — история power-duration curve: сезонный envelope vs острое окно 42д.
// Где acute ниже season — этот участок профиля просел (Allen & Coggan; Pinot 2011).
export interface PdcHistoryPoint {
  duration_s: number;
  best_all_w: number;            // сезонный потолок (365д)
  best_42d_w: number | null;     // острое окно (null, если усилия не было)
  faded_pct: number | null;      // (1 - best42d/bestAll) × 100
}

export interface PdcHistoryOut {
  rides_all: number;
  rides_42d: number;
  points: PdcHistoryPoint[];     // возрастание длительности; пусто = нет силовых
}

export function usePdcHistory() {
  return useQuery<PdcHistoryOut>({
    queryKey: ["athlete-analytics", "pdc-history"],
    queryFn: async () => {
      const r = await api.get<PdcHistoryOut>("/athlete/analytics/pdc-history");
      return r.data;
    },
    staleTime: 5 * 60_000,
  });
}


// T1-4 — понедельное распределение по зонам (Seiler): как смещается интенсивность.
export interface ZoneWeekPoint {
  week_start: string;
  percents: number[];   // [z1..z7], сумма ≤ 100 (внутри недели)
  total_minutes: number;
}

export interface ZonesTimelineOut {
  weeks: ZoneWeekPoint[];  // возрастание по дате; пусто = нет зональных данных
}

/**
 * Раскладка по зонам.
 *
 * `sports` НЕ опционален по смыслу: без него плавание, вело и бег
 * складываются в одну полосу, и «поляризация держится» скрывает, что
 * весь бег сидит в серой зоне. Умолчание оставлено ради вызовов, где
 * общая картина и нужна.
 */
export function useZonesTimeline(days = 90, sports?: string) {
  return useQuery<ZonesTimelineOut>({
    queryKey: ["athlete-analytics", "zones-timeline", days, sports ?? "all"],
    queryFn: async () => {
      const r = await api.get<ZonesTimelineOut>(
        `/athlete/analytics/zones-timeline?days=${days}` +
          (sports && sports !== "all" ? `&sport=${encodeURIComponent(sports)}` : ""),
      );
      return r.data;
    },
    staleTime: 5 * 60_000,
  });
}


// T2-1 — MMP power-profile: лучшая мощность на каждой длительности (Coggan), дата.
export interface MmpPoint {
  duration_s: number;
  best_w: number;
  achieved_on: string;   // ISO date пика
}

export interface MmpProfileOut {
  rides_analysed: number;
  curve: MmpPoint[];     // возрастание длительности; пусто = нет силовых
}

export function useMmpProfile(days = 90) {
  return useQuery<MmpProfileOut>({
    queryKey: ["athlete-analytics", "mmp-profile", days],
    queryFn: async () => {
      const r = await api.get<MmpProfileOut>(
        `/athlete/analytics/mmp-profile?days=${days}`,
      );
      return r.data;
    },
    staleTime: 5 * 60_000,
  });
}


// T2-2/T2-3 — эмпирический mFTP (0.95×20мин, Коган) + TTE (удержание ≥ mFTP).
export interface FtpModelOut {
  available: boolean;
  mftp_watts: number | null;
  tte_minutes: number | null;
  tte_capped: boolean;      // TTE упёрся в потолок сетки → выносливость выше
  rides_analysed: number;
}

export function useFtpModel(days = 90) {
  return useQuery<FtpModelOut>({
    queryKey: ["athlete-analytics", "ftp-model", days],
    queryFn: async () => {
      const r = await api.get<FtpModelOut>(`/athlete/analytics/ftp-model?days=${days}`);
      return r.data;
    },
    staleTime: 5 * 60_000,
  });
}


// T2-4 — iLevels: 7 индивид. силовых уровней из PD-кривой (верхние на реальных пиках).
export interface ILevel {
  n: number;
  key: string;          // active_recovery | endurance | tempo | threshold | vo2max | anaerobic | neuromuscular
  min_watts: number;
  max_watts: number;
  anchor: string;       // mftp | p5min | p1min | pmax
}

export interface ILevelsOut {
  available: boolean;
  mftp_watts: number | null;
  levels: ILevel[];
  // ae09: приняты ли уровни в зоны. Optional — version-skew со старым бэкендом.
  adopted?: boolean;
}

export function useILevels(days = 90) {
  return useQuery<ILevelsOut>({
    queryKey: ["athlete-analytics", "ilevels", days],
    queryFn: async () => {
      const r = await api.get<ILevelsOut>(`/athlete/analytics/ilevels?days=${days}`);
      return r.data;
    },
    staleTime: 5 * 60_000,
  });
}

// ae09: adopt/unadopt — iLevels как источник зон (source='ilevels', выше manual).
export function useAdoptILevels() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const r = await api.post<ILevelsOut>("/athlete/analytics/ilevels/adopt");
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["athlete-analytics", "ilevels"] });
      qc.invalidateQueries({ queryKey: ["athlete-zones"] });
    },
  });
}

export function useUnadoptILevels() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const r = await api.delete<{ removed: number }>("/athlete/analytics/ilevels/adopt");
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["athlete-analytics", "ilevels"] });
      qc.invalidateQueries({ queryKey: ["athlete-zones"] });
    },
  });
}


// T0-11 — OLS-линия регрессии веса (тот же fit, что LEA-скрин) + наклон г/нед.
export type WeightDirection = "losing" | "gaining" | "stable" | "none";

export interface WeightTrendOut {
  has_trend: boolean;
  pct_per_week: number | null;
  slope_g_per_week: number | null;
  direction: WeightDirection;
  start_date: string | null;
  end_date: string | null;
  start_weight: number | null;
  end_weight: number | null;
  points_used: number;
}

// T0-10 — сегодняшний lnRMSSD z-score относительно скользящей базы.
export type HrvZStatus = "suppressed" | "normal" | "elevated" | "none";

export interface HrvZscoreOut {
  z_score: number | null;
  threshold: number;
  status: HrvZStatus;
}

export function useHrvZscore() {
  return useQuery<HrvZscoreOut>({
    queryKey: ["athlete-analytics", "hrv-zscore"],
    queryFn: async () => {
      const r = await api.get<HrvZscoreOut>("/athlete/analytics/hrv-zscore");
      return r.data;
    },
    staleTime: 5 * 60_000,
  });
}

export function useWeightTrend(days = 90) {
  return useQuery<WeightTrendOut>({
    queryKey: ["athlete-analytics", "weight-trend", days],
    queryFn: async () => {
      const r = await api.get<WeightTrendOut>("/athlete/analytics/weight-trend", {
        params: { days },
      });
      return r.data;
    },
    staleTime: 5 * 60_000,
  });
}

// T5-1 — custom metrics: пользовательские формулы над рядами таймлайна.
// Парсинг/валидация на бэкенде (безопасный парсер, без eval).
export interface CustomMetricPoint {
  date: string;
  value: number;
}

export interface CustomMetricOut {
  id: number;
  label: string;
  formula: string;
  current_value?: number | null;
  series?: CustomMetricPoint[];
}

export interface CustomMetricsListOut {
  items: CustomMetricOut[];
  available_metrics: string[];
  limit: number;
}

const CUSTOM_METRICS_KEY = ["athlete-analytics", "custom-metrics"];

export function useCustomMetrics(days = 84) {
  return useQuery<CustomMetricsListOut>({
    queryKey: [...CUSTOM_METRICS_KEY, days],
    queryFn: async () => {
      const r = await api.get<CustomMetricsListOut>(
        `/athlete/analytics/custom-metrics?days=${days}`,
      );
      return r.data;
    },
    staleTime: 5 * 60_000,
  });
}

export function useCreateCustomMetric() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { label: string; formula: string }) => {
      const r = await api.post<CustomMetricOut>(
        "/athlete/analytics/custom-metrics", payload,
      );
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: CUSTOM_METRICS_KEY }),
  });
}

export function useDeleteCustomMetric() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/athlete/analytics/custom-metrics/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: CUSTOM_METRICS_KEY }),
  });
}

// ---------- Sleep stages (A6): REAL stages from intervals raw_data ----------
export interface SleepStagePoint {
  date: string;
  deep_h: number;
  rem_h: number;
  light_h: number;
  awake_h: number;
}

export function useSleepStages(days: number = 14) {
  return useQuery<SleepStagePoint[]>({
    queryKey: ["athlete-analytics", "sleep-stages", days],
    queryFn: async () => {
      const r = await api.get<SleepStagePoint[]>("/athlete/analytics/sleep-stages", {
        params: { days },
      });
      return r.data;
    },
    staleTime: 5 * 60_000,
  });
}

// ---------- Weather history (A7): real wind + humidity (Open-Meteo) ----------
export interface WeatherDayPoint {
  date: string;
  wind_kmh: number | null;
  humidity_pct: number | null;
}

// On-course weather from intervals activity payloads (more accurate than home).
export interface ActivityWeatherOut {
  sessions: number;
  humidity_pct: number | null;
  feels_like_c: number | null;
  dew_point_c: number | null;
  latest_conditions: string | null;
}

export function useActivityWeather(days: number = 60) {
  return useQuery<ActivityWeatherOut>({
    queryKey: ["athlete-analytics", "activity-weather", days],
    queryFn: async () => {
      const r = await api.get<ActivityWeatherOut>("/athlete/analytics/activity-weather", { params: { days } });
      return r.data;
    },
    staleTime: 30 * 60_000,
  });
}

export function useWeatherHistory(days: number = 60) {
  return useQuery<WeatherDayPoint[]>({
    queryKey: ["athlete-analytics", "weather-history", days],
    queryFn: async () => {
      const r = await api.get<WeatherDayPoint[]>("/athlete/analytics/weather-history", {
        params: { days },
      });
      return r.data;
    },
    staleTime: 30 * 60_000,
  });
}

// ---------- Terrain / grade (our model from streams) ----------
export interface TerrainOut {
  activities: number;
  total_climb_m: number;
  total_distance_m: number;
  climb_pct: number;
  steepest_sustained_pct: number;
  bands_m: Record<string, number>;
}

export function useTerrain(days: number = 60) {
  return useQuery<TerrainOut>({
    queryKey: ["athlete-analytics", "terrain", days],
    queryFn: async () => {
      const r = await api.get<TerrainOut>("/athlete/analytics/terrain", { params: { days } });
      return r.data;
    },
    staleTime: 10 * 60_000,
  });
}

// ---------- Intervals modelled power (cross-check) ----------
export interface IntervalsPowerOut {
  available: boolean;
  date: string | null;
  sport: string | null;
  eftp: number | null;
  wprime: number | null;
  pmax: number | null;
}

export function useIntervalsPower() {
  return useQuery<IntervalsPowerOut>({
    queryKey: ["athlete-analytics", "intervals-power"],
    queryFn: async () => {
      const r = await api.get<IntervalsPowerOut>("/athlete/analytics/intervals-power");
      return r.data;
    },
    staleTime: 10 * 60_000,
  });
}

// ---------- Aerobic decoupling (our model from streams) ----------
export interface DecouplingPoint {
  date: string;
  decoupling_pct: number;
  intervals_pct?: number | null;
}
export interface DecouplingOut {
  points: DecouplingPoint[];
  avg_pct: number | null;
  latest_pct: number | null;
  intervals_avg_pct?: number | null;
  divergent?: boolean;
  /**
   * Чем посчитано: `pw_hr` (ватты/пульс) и/или `pa_hr` (скорость/пульс).
   * Два вида сразу — среднее смешало РАЗНЫЕ величины, и порог Фрила,
   * откалиброванный внутри одной дисциплины, к нему неприменим.
   */
  kinds?: string[];
}

export function useDecoupling(days: number = 60, sports?: SportFilter) {
  const sp = sportsParam(sports ?? []);
  return useQuery<DecouplingOut>({
    queryKey: ["athlete-analytics", "decoupling", days, sp ?? "all"],
    queryFn: async () => {
      const r = await api.get<DecouplingOut>("/athlete/analytics/decoupling", {
        params: { days, ...(sp ? { sport: sp } : {}) },
      });
      return r.data;
    },
    staleTime: 10 * 60_000,
  });
}

// ---------- Climb performance (our model: power on climbs vs flats) ----------
export interface ClimbPerfOut {
  available: boolean;
  activities: number;
  flat_avg_w: number | null;
  climb_avg_w: number | null;
}

export function useClimbPerformance(days: number = 90) {
  return useQuery<ClimbPerfOut>({
    queryKey: ["athlete-analytics", "climb-performance", days],
    queryFn: async () => {
      const r = await api.get<ClimbPerfOut>("/athlete/analytics/climb-performance", { params: { days } });
      return r.data;
    },
    staleTime: 10 * 60_000,
  });
}

// ---------- Pacing / negative-split (our model from streams) ----------
export interface PacingPoint {
  date: string;
  split_pct: number;
}
export interface PacingOut {
  points: PacingPoint[];
  avg_pct: number | null;
  negative_split_share: number | null;
}

export function usePacing(days: number = 60, sports?: SportFilter) {
  // Раскладка половин у бега и у велосипеда живёт по разным правилам:
  // на шоссе вторую половину диктует ветер и рельеф, в беге — сам атлет.
  // Смешанное среднее не отвечает ни на один из двух вопросов.
  const sp = sportsParam(sports ?? []);
  return useQuery<PacingOut>({
    queryKey: ["athlete-analytics", "pacing", days, sp ?? "all"],
    queryFn: async () => {
      const r = await api.get<PacingOut>("/athlete/analytics/pacing", {
        params: { days, ...(sp ? { sport: sp } : {}) },
      });
      return r.data;
    },
    staleTime: 10 * 60_000,
  });
}

// ---------- Wellness extras (recovery signals from intervals raw_data) ----------
export interface WellnessExtrasOut {
  available: boolean;
  spo2: number | null;
  hrv_sdnn: number | null;
  avg_sleeping_hr: number | null;
  soreness: number | null;
  mood: number | null;
  motivation: number | null;
  steps: number | null;
}

export function useWellnessExtras(days: number = 14) {
  return useQuery<WellnessExtrasOut>({
    queryKey: ["athlete-analytics", "wellness-extras", days],
    queryFn: async () => {
      const r = await api.get<WellnessExtrasOut>("/athlete/analytics/wellness-extras", { params: { days } });
      return r.data;
    },
    staleTime: 10 * 60_000,
  });
}

// ---------- Кривая периода: конверт лучших средних для ЛЮБОГО вида спорта ----------
//
// До этого кривые периода были только велосипедные (mmp-profile, pdc-history,
// durability-curve стоят на модели критической мощности). Здесь — конверт,
// который никакой вело-физики не требует и потому доступен бегу, плаванию и
// всему, где есть хотя бы пульс.

export type CurveMetricKey = "power" | "pace" | "hr";
export const CURVE_COMPARE_KINDS = ["prev", "year", "none"] as const;
export type CurveCompareKind = (typeof CURVE_COMPARE_KINDS)[number];

export interface PeriodCurvePoint {
  duration_s: number;
  value: number;
  /** Сколько тренировок периода смогли ответить на это окно. */
  attempts?: number;
}

export interface PeriodCurveSide {
  from_date: string;
  to_date: string;
  points: PeriodCurvePoint[];
  workouts_used: number;
  capped: boolean;
}

export interface PeriodCurve {
  metric: CurveMetricKey;
  unit: string;
  resolution_s: number | null;
  current: PeriodCurveSide;
  compare: PeriodCurveSide | null;
  compare_kind: CurveCompareKind | null;
  /** метрика → сколько тренировок периода её имеют. Переключатель строится по нему. */
  available: Partial<Record<CurveMetricKey, number>>;
}

/**
 * Кривая периода. `metric` не задан — бэкенд выберет по преобладающей
 * дисциплине окна (мощность на велосипеде, темп в остальном).
 */
export function usePeriodCurve(
  range: Range,
  opts: { metric?: CurveMetricKey | null; sports?: SportFilter; compare?: CurveCompareKind } = {},
) {
  const sp = sportsParam(opts.sports ?? []);
  const compare = opts.compare ?? "prev";
  return useQuery<PeriodCurve>({
    queryKey: [
      "athlete-analytics", "period-curve",
      range.from, range.to, opts.metric ?? "auto", sp ?? "all", compare,
    ],
    queryFn: async () => {
      const params: Record<string, string> = {
        from_date: range.from, to_date: range.to, compare,
      };
      if (opts.metric) params.metric = opts.metric;
      if (sp) params.sport = sp;
      const r = await api.get<PeriodCurve>("/athlete/analytics/period-curve", { params });
      return r.data;
    },
    // Конверт периода меняется только с новой тренировкой — держим дольше
    // обычного, чтобы переключение метрик не гоняло потоки заново.
    staleTime: 10 * 60_000,
  });
}

// ---------- События на кривых: что происходило, пока метрика двигалась ----------

export interface AnalysisEventItem {
  date: string;
  kind: "race" | "injury" | "illness" | "test" | "travel" | "note";
  label: string;
  end_date: string | null;
}

export interface AnalysisEvents {
  items: AnalysisEventItem[];
  /** Меток больше, чем показано: потолок не должен выглядеть как «столько и было». */
  capped: boolean;
}

/**
 * Метки событий периода. Один запрос обслуживает все карточки с одинаковым
 * окном — react-query склеит их по ключу, и слой меток не стоит ничего сверх
 * первой карточки.
 */
export function useAnalysisEvents(range: Range) {
  return useQuery<AnalysisEvents>({
    queryKey: ["athlete-analytics", "events", range.from, range.to],
    queryFn: async () => {
      const r = await api.get<AnalysisEvents>("/athlete/analytics/events", {
        params: { from_date: range.from, to_date: range.to },
      });
      return r.data;
    },
    staleTime: 10 * 60_000,
  });
}

// ─── триатлон: три ноги + цена перехода вело→бег ─────────────────────────────

export interface TriLeg {
  leg: "swim" | "bike" | "run";
  minutes: number;
  share: number;          // доля от суммы трёх ног, 0..1
  expected_share: number; // канон дистанции
  drift_pp: number;       // факт − канон, в процентных пунктах
}

export interface TriBrick {
  date: string;
  brick_pair_id: number | null;
  bike_minutes: number | null;
  run_km: number | null;
  run_pace_s_per_km: number | null;
  cost_pct: number | null;
  category: "strong" | "normal" | "high" | "excessive" | null;
}

export interface TriWeekDay {
  date: string;
  /** нога → минуты; включает strength — полоса про день, а не про сплит. */
  legs: Partial<Record<"swim" | "bike" | "run" | "strength", number>>;
  is_brick: boolean;
  is_race: boolean;
  done: boolean;
}

export interface TriathlonView {
  from_date: string;
  to_date: string;
  legs: TriLeg[];
  week: TriWeekDay[];
  total_minutes: number;
  split_available: boolean;
  baseline_run_pace_s_per_km: number | null;
  bricks: TriBrick[];
  /** Сессии, записанные одной строкой: ноги не различить, но они были. */
  unsplit_bricks: number;
  race_format: string | null;
  bike_if_limit: number | null;
  /** Доли трёх ног по неделям — история баланса, а не один срез за период. */
  weekly?: TriWeeklySplit[];
}

/**
 * Неделя баланса. `partial` — обрезана краем окна, `low_volume` — объёма мало
 * и доли шумные. Оба флага помечают, а не прячут: выброшенная неделя
 * читается как «данных нет», хотя была лёгкая или текущая.
 */
export interface TriWeeklySplit {
  week_start: string;
  shares: Record<string, number>;
  minutes: Record<string, number>;
  total_minutes: number;
  partial: boolean;
  low_volume: boolean;
}

export function useTriathlon(range: Range) {
  return useQuery<TriathlonView>({
    queryKey: ["athlete-analytics", "triathlon", range.from, range.to],
    queryFn: async () => {
      const r = await api.get<TriathlonView>("/athlete/analytics/triathlon", {
        params: { from_date: range.from, to_date: range.to },
      });
      return r.data;
    },
    staleTime: 10 * 60_000,
  });
}

// ─── сила: объём, баланс групп, частота ─────────────────────────────────────

export interface StrengthMuscle {
  muscle: string;
  sets: number;
  share: number;
}

export interface StrengthExercise {
  slug: string;
  name: string | null;
  sets: number;
  reps: number;
  /** null — вес нигде не записан (собственный вес или %1RM), а не «ноль кг». */
  tonnage_kg: number | null;
  sessions: number;
}

export interface StrengthView {
  from_date: string;
  to_date: string;
  sessions: number;
  minutes: number;
  median_gap_days: number | null;
  last_session: string | null;
  sessions_with_structure: number;
  /** Силовые без записанного состава: объёма по ним нет, но они были. */
  sessions_without_structure: number;
  total_sets: number;
  total_reps: number;
  tonnage_kg: number | null;
  sets_without_weight: number;
  sets_pct_1rm: number;
  muscles: StrengthMuscle[];
  exercises: StrengthExercise[];
}

export function useStrength(range: Range) {
  return useQuery<StrengthView>({
    queryKey: ["athlete-analytics", "strength", range.from, range.to],
    queryFn: async () => {
      const r = await api.get<StrengthView>("/athlete/analytics/strength", {
        params: { from_date: range.from, to_date: range.to },
      });
      return r.data;
    },
    staleTime: 10 * 60_000,
  });
}

// ─── Hyrox: станции за период ───────────────────────────────────────────────

export interface HyroxStationPeriod {
  station: string;
  sessions: number;
  avg_seconds: number;
  benchmark_seconds: number;
  avg_delta_seconds: number;
  pct_of_benchmark: number | null;
  best_seconds: number;
}

export interface HyroxPeriodView {
  from_date: string;
  to_date: string;
  level: string;
  sessions: number;
  sessions_parsed: number;
  /** Гонки, чьи круги не сложились в восемь станций — разобрать нельзя. */
  sessions_unparsed: number;
  stations: HyroxStationPeriod[];
  /** null, пока разобрана одна гонка или атлет быстрее ориентира везде. */
  limiter: string | null;
}

export function useHyroxPeriod(range: Range, level = "open") {
  return useQuery<HyroxPeriodView>({
    queryKey: ["athlete-analytics", "hyrox", range.from, range.to, level],
    queryFn: async () => {
      const r = await api.get<HyroxPeriodView>("/athlete/analytics/hyrox", {
        params: { from_date: range.from, to_date: range.to, level },
      });
      return r.data;
    },
    staleTime: 10 * 60_000,
  });
}

// ─── плавание: кривая «лучшее время на дистанции» ───────────────────────────

export interface SwimCurvePoint {
  distance_m: number;
  seconds: number;
  pace_sec_per_100m: number;
}

/**
 * Средний темп заплыва — ДРУГАЯ величина, чем лучшее время на дистанции.
 * В неё входят разминка, отдых на бортике и заминка. Отдельный ряд и
 * отдельная подпись: среднее по тренировке, показанное как рекорд, — подмена.
 */
export interface SwimSessionPace {
  date: string;
  workout_id: number;
  distance_m: number;
  duration_s: number;
  pace_sec_per_100m: number;
}

export interface SwimCurveView {
  from_date: string;
  to_date: string;
  points: SwimCurvePoint[];
  workouts_used: number;
  capped: boolean;
  /** null — сравнивать не с чем, а не «нулевое улучшение». */
  compare_points: SwimCurvePoint[] | null;
  compare_from: string | null;
  compare_to: string | null;
  /** Запасной слой для бассейна, где потоков нет. Пусто — заплывов не было. */
  sessions?: SwimSessionPace[];
  /** Порог из профиля: без него темп не читается — быстро это или медленно. */
  css_sec_per_100m?: number | null;
}

export function useSwimCurve(range: Range) {
  return useQuery<SwimCurveView>({
    queryKey: ["athlete-analytics", "swim-curve", range.from, range.to],
    queryFn: async () => {
      const r = await api.get<SwimCurveView>("/athlete/analytics/swim-curve", {
        params: { from_date: range.from, to_date: range.to },
      });
      return r.data;
    },
    staleTime: 10 * 60_000,
  });
}

// ─── нормы метрик: границы для полос на графиках ────────────────────────────

export interface MetricNorm {
  metric: string;
  sport: string | null;
  /** null — у метрики нет нижней границы: меньше значит лучше. */
  good_low: number | null;
  good_high: number | null;
  critical_high: number | null;
  source: string | null;
}

export function useMetricNorms() {
  return useQuery<{ norms: MetricNorm[] }>({
    queryKey: ["athlete-analytics", "norms"],
    queryFn: async () => (await api.get<{ norms: MetricNorm[] }>("/athlete/analytics/norms")).data,
    // Границы меняются только со сменой вида спорта — держим долго.
    staleTime: 60 * 60_000,
  });
}

/** Норма одной метрики, или null — рисовать полосу тогда нечем. */
export function findNorm(norms: MetricNorm[] | undefined, metric: string): MetricNorm | null {
  return norms?.find((n) => n.metric === metric) ?? null;
}
